import axios from 'axios';
import * as logger from 'loglevel';
import { useCallback } from 'react';
import { annotateAndLogError } from '../../../helpers/error';
import { journeyTypeToLongValue, getQueryParams } from '../../../helpers/utils';
import { isSSR } from '@front10/utils';
import { formatDate, formatTime, formatValue } from './helpers';
import { BookingPanelState } from '../../../components/containers/VerticalBookingContainer/BookingPanel';
import { count } from '../../../components/lab/utils';
import { useTracking } from './TrackingContext';
import { publish } from './pubsub';
import { doRedirect } from './redirects';

const journeyTypes: Record<string, string | undefined> = journeyTypeToLongValue();

export enum VerticalBookingActions {
  SEARCH_FLIGHTS = 1001,
  CHANGE_ORIGIN,
  CHANGE_DESTINATION,
  POPUP_OPENED,
  CHANGE_DEPARTURE_DATE,
  CHANGE_RETURN_DATE,
  CHANGE_AIRLINE,
  POPUP_CLOSE,
  CHANGE_JOURNEY_TYPE
}

export type TrackingPayload = BookingPanelState & {
  trackingParameters?: Record<string, any>;
  extraInfo?: Record<string, any>;
  hotelId?: string;
};

export type TrackingAction = {
  type: VerticalBookingActions;
  payload: TrackingPayload;
};

function isEnableTrackingEvent(nameEvent: string, enableEventsName: Array<string>) {
  for (let index = 0; index < enableEventsName.length; index++) {
    const element = enableEventsName[index];
    if (element.toUpperCase() === nameEvent) {
      return true;
    }
  }
  return false;
}

function sendTrackingEvent(
  actionType: string,
  eventName: string,
  baseline: any,
  filteredData: any,
  enableEventsName: Array<string>
) {
  if (enableEventsName.length !== 0) {
    if (isEnableTrackingEvent(actionType, enableEventsName)) {
      publish(eventName, {
        ...baseline,
        ...filteredData
      });
    }
  } else {
    publish(eventName, {
      ...baseline,
      ...filteredData
    });
  }
}

/**
 * This function get params in url that contains in white list
 * @param {*} trackingParametersWhitelist
 */
function getUrlParameters() {
  if (isSSR()) {
    return {};
  }
  const query =
    window.location.search && window.location.search.startsWith('?')
      ? window.location.search.substring(1)
      : window.location.search;
  return getQueryParams(query);
}

function filterTrackingParameters(allowList: string[], params: Record<string, unknown>) {
  if (isSSR()) {
    return {};
  }
  const trackingParameters: Record<string, unknown> = {};
  Object.keys(params).forEach((key) => {
    if (allowList.includes(key) && params[key] !== '') {
      if (key === 'utm_campaign') {
        trackingParameters['wl_promo'] = params[key];
      } else {
        trackingParameters[key] = params[key];
      }
    }
  });
  return trackingParameters;
}

/**
 * Build the base data tracking
 * @param state
 * @param container
 */
function buildBaseLineTrackingData(state: TrackingPayload, container: ContainerTrackingData) {
  const dates = state.dates;
  let passengers;
  if (state.passengers !== undefined) {
    passengers = state.passengers;
  } else {
    if (state.passengerInfo !== undefined) {
      passengers = {
        passenger1: state.passengerInfo?.count[0]
      };
    } else {
      passengers = {
        passenger1: 1
      };
    }
  }
  const tmp: any = { ...state };

  if (state.type.includes('hotel')) {
    if (
      state.rooms &&
      tmp.businessRules &&
      tmp.businessRules.rooms &&
      tmp.businessRules.rooms.options &&
      tmp.businessRules.rooms.options.length !== 0 &&
      tmp.businessRules.rooms.options[0].type
    ) {
      const typeOrder = ['Adult', 'Children', 'Youths', 'Infants'];
      let newValue: any = {
        passenger1: 0,
        passenger2: 0,
        passenger3: 0,
        passenger4: 0
      };
      const enableShowDetailedAgeOptions =
        tmp.businessRules.rooms.options.findIndex((e: any) => e.showDetailedAgeOptions) !== -1;
      if (enableShowDetailedAgeOptions) {
        newValue = { ...newValue, passenger5: 0 };
      }
      state.rooms.forEach((room, k) => {
        for (let index = 0; index < typeOrder.length; index++) {
          const type = typeOrder[index];
          for (let i = 0; i < tmp.businessRules.rooms.options.length; i++) {
            const element = tmp.businessRules.rooms.options[i];
            if (element.showDetailedAgeOptions) {
              if (type === element.type && state.rooms) {
                for (let j = 0; j < state.rooms[k].ages[i].length; j++) {
                  const element = state.rooms[k].ages[i][j];
                  if (element === 0) {
                    newValue[`passenger4`] = newValue[`passenger4`] + 1;
                  } else if (element === 1) {
                    newValue[`passenger5`] = newValue[`passenger5`] + 1;
                  } else {
                    newValue[`passenger2`] = newValue[`passenger2`] + 1;
                  }
                }
              }
            } else {
              if (type === element.type && state.rooms) {
                newValue[`passenger${index + 1}`] =
                  newValue[`passenger${index + 1}`] + state.rooms[k].count[i];
              }
            }
          }
        }
      });
      passengers = newValue;
    } else {
      if (state.rooms) {
        const draftPassengers = {
          passenger1: 0,
          passenger2: 0
        };
        state.rooms.forEach((room) => {
          draftPassengers.passenger1 += room.count[0];
          for (let index = 1; index < room.count.length; index++) {
            draftPassengers.passenger2 += room.count[index];
          }
        });
        passengers = draftPassengers;
      }
    }
  } else {
    if (
      state.passengers &&
      tmp.businessRules &&
      tmp.businessRules.travelers &&
      tmp.businessRules.travelers.options &&
      tmp.businessRules.travelers.options.length !== 0 &&
      tmp.businessRules.travelers.options[0].type
    ) {
      const enableShowDetailedAgeOptions =
        tmp.businessRules.travelers.options.findIndex((e: any) => e.showDetailedAgeOptions) !== -1;
      if (enableShowDetailedAgeOptions) {
        let newValue: any = {};
        // TODO: These changes only apply to the Flight tab, a future iteration is to do it in the packages tab
        const childrenKey = tmp.businessRules.travelers.options
          .filter((item: $TSFixMe) => item.type === 'Children')
          .pop();
        for (let i = 0; i < tmp.businessRules.travelers.options.length; i++) {
          const element = tmp.businessRules.travelers.options[i];
          if (element.showDetailedAgeOptions) {
            const tmp: any = { ...state.rooms };
            if (
              state.rooms &&
              state.rooms &&
              state.rooms.length !== 0 &&
              state.rooms[0].ages &&
              state.rooms[0].ages.length !== 0
            ) {
              for (let k = 0; k < tmp[0].ages[i].length; k++) {
                const tempAge = tmp[0].ages[i][k];
                if (Array.isArray(element.param)) {
                  const lap = element.param[0].split('_lap').shift();
                  const seat = element.param[1].split('_seat').shift();

                  if (tempAge === 0) {
                    //Infants in laps
                    newValue[`${lap}`] = newValue[`${lap}`] ? newValue[`${lap}`] + 1 : 1;
                  } else if (tempAge === 1) {
                    // Infants in seat
                    newValue[`${seat}`] = newValue[`${seat}`] ? newValue[`${seat}`] + 1 : 1;
                  } else {
                    // Finding for the key of Children to add one more
                    if (childrenKey && childrenKey.param) {
                      newValue[`${childrenKey.param}`] = newValue[`${childrenKey.param}`]
                        ? newValue[`${childrenKey.param}`] + 1
                        : 1;
                    }
                  }
                } else {
                  if (childrenKey && childrenKey.param) {
                    newValue[`${childrenKey.param}`] = newValue[`${childrenKey.param}`]
                      ? newValue[`${childrenKey.param}`] + 1
                      : 1;
                  }
                }
              }
            }
          } else {
            newValue[`${element.param}`] = state.passengers[element.name];
          }
        }
        passengers = newValue;
      } else if (state.passengers) {
        let newValue: any = {};
        tmp.businessRules.travelers.options.forEach((element: $TSFixMe) => {
          if (state.passengers) {
            newValue[`${element.param}`] = state.passengers[element.name];
          }
        });
        passengers = newValue;
      }
    }
  }

  const passengerCount = count(Object.values(passengers));
  return {
    currencyCode: container?.currencyCode,
    departureDate: dates?.start
      ? formatDate(dates.start)
      : formatDate(container.departureDateStart),
    destinationAirportIataCode: formatValue(state.destination?.value),
    fareClass: formatValue(state.travelClass),
    flexibleDates: dates?.isFlexibleDates === undefined ? 'n/a' : dates?.isFlexibleDates,
    journeyType: formatValue(state.flightType),
    originAirportIataCode: formatValue(state.origin?.value),
    promoCode: formatValue(container.promoCode),
    returnDate: formatDate(dates?.end),
    siteEdition: container?.siteEdition,
    passengerCount,
    passengers,
    tagName: container.tagName,
    excludeBasicEconomy: formatValue(
      state.excludeBasicEconomy !== undefined ? !state.excludeBasicEconomy : undefined
    )
  };
}

function getJourneyTypeLongValue(value: string) {
  return journeyTypes[value] ?? value;
}

function formatIataCode(originIataCode: string, ptrefix: string) {
  return `${ptrefix}:${originIataCode}`;
}

function getVacationsData(data: any) {
  const vacations = {
    flight: data.type.includes('flight'),
    hotel: data.type.includes('hotel'),
    car: data.type.includes('car'),
    activity: data.type.includes('activity')
  };

  if (data.type.includes('hotel')) {
    if (
      data.rooms &&
      data.businessRules &&
      data.businessRules.rooms &&
      data.businessRules.rooms.options &&
      data.businessRules.rooms.options.length !== 0 &&
      data.businessRules.rooms.options[0].type
    ) {
      const typeOrder = ['Adult', 'Children', 'Youths', 'Infants'];
      return {
        vacations: {
          ...vacations,
          numRooms: data.rooms?.length ?? 1,
          rooms: data.rooms?.map((room: any) => {
            let numAdults = 1,
              numChildren = 0,
              numYouths = 0,
              numInfants = 0,
              numInfantsInSeat = 0,
              numInfantsInLap = 0;
            let wasContChildrenAges = false;
            let childAges = [];
            for (let index = 0; index < typeOrder.length; index++) {
              const type = typeOrder[index];
              for (let i = 0; i < data.businessRules.rooms.options.length; i++) {
                const element = data.businessRules.rooms.options[i];
                if (type === element.type && data.rooms) {
                  switch (index) {
                    case 0:
                      numAdults = room.count[i];
                      break;
                    case 1:
                      numChildren = room.count[i];
                      if (
                        room.ages?.[i] !== null &&
                        room.ages?.[i] !== undefined &&
                        Array.isArray(room.ages?.[i]) &&
                        room.ages?.[i]?.length !== 0
                      ) {
                        childAges.push(...room.ages?.[i]);
                      }
                      break;
                    case 2:
                      numYouths = room.count[i];
                      break;
                    case 3: {
                      if (element.showDetailedAgeOptions) {
                        for (let j = 0; j < room.ages[i].length; j++) {
                          const element = room.ages[i][j];
                          if (element === 0) {
                            numInfantsInLap = numInfantsInLap + 1;
                          } else if (element === 1) {
                            numInfantsInSeat = numInfantsInSeat + 1;
                          } else {
                            numChildren = numChildren + 1;
                            if (
                              room.ages?.[i] !== null &&
                              room.ages?.[i] !== undefined &&
                              Array.isArray(room.ages?.[i]) &&
                              room.ages?.[i]?.length !== 0
                            ) {
                              const newAges = room.ages?.[i].filter((age: number) => age >= 2);
                              if (!wasContChildrenAges) {
                                childAges.push(...newAges);
                                wasContChildrenAges = true;
                              }
                            }
                          }
                        }
                      } else {
                        numInfants = room.count[i];
                      }
                      break;
                    }
                    default:
                      break;
                  }
                }
              }
            }
            const enableShowDetailedAgeOptions =
              data.businessRules.rooms.options.findIndex((e: any) => e.showDetailedAgeOptions) !==
              -1;
            if (enableShowDetailedAgeOptions) {
              return {
                numAdults: numAdults,
                numChildren: numChildren,
                numYouths: numYouths,
                numInfantsInLaps: numInfantsInLap,
                numInfantsInSeats: numInfantsInSeat,
                childAges: childAges
              };
            }
            const infantsConfigurationIndex = data.businessRules.rooms.options.findIndex(
              (e: any) => e.type === 'Infants'
            );
            if (infantsConfigurationIndex !== -1) {
              const infantsConfiguration =
                data.businessRules.rooms.options[infantsConfigurationIndex];
              if (infantsConfiguration?.allInfantsAsInfantsInLaps === true) {
                return {
                  numAdults: numAdults,
                  numChildren: numChildren,
                  numYouths: numYouths,
                  numInfantsInLaps: numInfants,
                  numInfantsInSeats: 0,
                  childAges: childAges
                };
              }
            }
            return {
              numAdults: numAdults,
              numChildren: numChildren,
              numYouths: numYouths,
              numInfants: numInfants,
              childAges: childAges
            };
          })
        }
      };
    } else {
      return {
        vacations: {
          ...vacations,
          numRooms: data.rooms?.length ?? 1,
          rooms: data.rooms?.map((room: any) => ({
            numSeniors: 0,
            numInfantsInSeats: 0,
            numInfantsInLaps: 0,
            numAdults: room.count[0],
            numChildren: room.count[1] + room.count[2],
            ...(room.count[1] + room.count[2] && {
              childAges: [...room.ages?.[1], ...room.ages?.[2]]
            })
          })) ?? [{ numAdults: 1, numChildren: 0 }],
          ...(data?.hotelId ? { hotelId: data?.hotelId } : {})
        }
      };
    }
  } else {
    return {
      vacations
    };
  }
}

function buildQuery(container: ContainerTrackingData, data: TrackingPayload) {
  const {
    deviceCategory,
    siteEdition,
    language,
    currencyCode,
    v2DeepLinkApiSettings: { params }
  } = container;
  // Get external platfom parameters
  const { extraInfo, trackingParameters: trackingParametersParams } = params;

  if (!deviceCategory) {
    logger.warn('INTEGRATION__DEEPLINK: **device** is not defined.');
  }
  if (!siteEdition) {
    logger.warn('INTEGRATION__DEEPLINK: **siteEdition** is not defined.');
  }
  if (!extraInfo) {
    logger.warn('INTEGRATION__DEEPLINK: **extraInfo** is not defined.');
  }

  if (!data) {
    logger.warn('INTEGRATION__DEEPLINK: **submit data** is empty.');
  }

  let trackingParameters: Record<string, unknown> | undefined = undefined;

  const allowList = container.v2DeepLinkApiSettings.trackingParametersWhitelist;
  if (allowList && Array.isArray(allowList)) {
    const unfilteredParameters = {
      ...getUrlParameters(),
      ...data?.extraInfo,
      ...data?.trackingParameters,
      ...trackingParametersParams
    };
    trackingParameters = filterTrackingParameters(allowList, unfilteredParameters);
  }

  const flightType = data.flightType ? getJourneyTypeLongValue(data.flightType) : data.flightType;
  let query: $TSFixMe = {
    origin: data.origin?.value ?? '',
    destination: data.destination?.value ?? '',
    departure: data.dates?.start ? formatDate(data.dates.start) : '',
    return: data.flightType !== 'ow' && data.dates?.end ? formatDate(data.dates.end) : '',
    departureTime:
      data.type.includes('car') && data.dates?.startTime
        ? formatTime(data.dates?.startTime)
        : undefined,
    returnTime:
      data.type.includes('car') && data.dates?.endTime
        ? formatTime(data.dates?.endTime)
        : undefined,
    flightType: flightType ?? '',
    siteEdition,
    language,
    passengers: data.passengers,
    // rules: data.rules,
    currencyCode: currencyCode ?? '',
    ...(data.travelClass && { travelClass: data.travelClass }),
    ...(data.promoCode && { promoCode: data.promoCode }),
    flexibleDates: data.dates?.isFlexibleDates ?? false,
    device: deviceCategory?.toUpperCase() || 'DESKTOP',
    ...getVacationsData(data),
    ...((data.origin?.zoneId || data.origin?.locationId) && {
      originInfo: {
        extraInfo: {
          zoneId: data.origin.zoneId,
          locationId: data.origin.locationId
        }
      }
    }),
    ...((data.destination?.zoneId || data.destination?.locationId) && {
      destinationInfo: {
        extraInfo: {
          zoneId: data.destination.zoneId,
          locationId: data.destination.locationId
        }
      }
    }),
    ...(data.origin?.city && {
      originInfo: {
        city: data.origin?.city,
        extraInfo: { type: data.origin?.extraInfo?.type }
      }
    }),
    ...(data.destination?.city && {
      destinationInfo: {
        city: data.destination?.city,
        extraInfo: { type: data.destination?.extraInfo?.type }
      }
    }),
    extraInfo: {
      ...extraInfo,
      ...(data.hotelCategory && { hotelCategories: data.hotelCategory }),
      ...(data.hotel && { hotelName: data.hotel }),
      ...{
        excludeBasicEconomy:
          data.excludeBasicEconomy !== undefined ? !data.excludeBasicEconomy : undefined
      },
      ...(data.carType && data.carType !== 'all' && { carType: data.carType })
    },
    trackingParameters: trackingParameters ?? {}
  };

  if (container.v2DeepLinkApiSettings.params.optionalsDeeplinkParameters !== undefined) {
    const optionalsDeeplinkParameters =
      container.v2DeepLinkApiSettings.params.optionalsDeeplinkParameters;

    for (let index = 0; index < optionalsDeeplinkParameters.length; index++) {
      const element = optionalsDeeplinkParameters[index];
      switch (element.type) {
        case 'area': {
          const data = element.data;
          for (let i = 0; i < data.length; i++) {
            const e = data[i];
            switch (e) {
              case 'area1': {
                query['vacations'] = {
                  ...query['vacations'],
                  area1: formatIataCode(query['origin'], element.prefix)
                };
                break;
              }
              case 'area2': {
                query['vacations'] = {
                  ...query['vacations'],
                  area2: formatIataCode(query['destination'], element.prefix)
                };
                break;
              }
            }
          }
          break;
        }
        case 'date': {
          for (let i = 0; i < element.data.length; i++) {
            const e = element.data[i];
            switch (e) {
              case 'date1': {
                query['vacations'] = {
                  ...query['vacations'],
                  date1: formatDate(data.dates?.start, element.dateFormat)
                };
                break;
              }
              case 'date2': {
                query['vacations'] = {
                  ...query['vacations'],
                  date2: formatDate(data.dates?.end, element.dateFormat)
                };
                break;
              }
            }
          }
          break;
        }
        case 'air_room_car_radio': {
          query['vacations'] = {
            ...query['vacations'],
            air_room_car_radio: 'air_room'
          };
          break;
        }
      }
    }
  }

  if (data.hotelId !== undefined) {
    query = { ...query, vacations: { ...query?.vacations, hotelId: data.hotelId } };
  }

  if (data.airlines !== undefined) {
    query = { ...query, airline: data.airlines?.value };
  }

  return query;
}

function redirect(container: ContainerTrackingData, data: TrackingPayload) {
  const query = buildQuery(container, data);
  const {
    v2DeepLinkApiSettings,
    airlineIataCode: airlineCodeFromPrePopulation,
    forceAirline: forceAirlineFromPrePopulation
  } = container;
  // INFO: The filter's airlineCode is given priority in case the user changes the airline in the popup
  const { airlines: { value: airlineCodeFromFilter } = {}, airline: airlineCodeFromFare } = data;
  const airlineCode =
    airlineCodeFromFilter ||
    airlineCodeFromFare ||
    forceAirlineFromPrePopulation ||
    airlineCodeFromPrePopulation;
  const newV2DeepLinkApiSettings = { ...v2DeepLinkApiSettings };
  let error = '';
  if (!v2DeepLinkApiSettings.method) {
    error += 'INTEGRATION__DEEPLINK: **method** is not defined. ';
  }
  if (!v2DeepLinkApiSettings.endPoint) {
    error += 'INTEGRATION__DEEPLINK: **endPoint** is not defined.';
  }
  if (!airlineCode) {
    error += 'INTEGRATION__DEEPLINK: **airlineCode** is not defined.';
  }
  if (
    newV2DeepLinkApiSettings.endPoint.search('%%airlineCode%%') !== -1 ||
    newV2DeepLinkApiSettings.endPoint.search('@@airlineIataCode@@') !== -1
  ) {
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '%%airlineCode%%',
      airlineCode
    );
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '@@airlineIataCode@@',
      airlineCode
    );
  }
  if (!newV2DeepLinkApiSettings.headers) {
    newV2DeepLinkApiSettings.headers = {};
  }
  if (error) {
    logger.error(error);
  } else {
    axios({
      method: newV2DeepLinkApiSettings.method,
      url: `${newV2DeepLinkApiSettings.endPoint}`,
      headers: { ...newV2DeepLinkApiSettings.headers },
      data: query
    })
      .then((response) => doRedirect(response.data))
      .catch((err) => {
        logger.error(
          annotateAndLogError(
            err,
            'INTEGRATION__DEEPLINK',
            false,
            false,
            container.integrationSettings,
            container.meta
          )
        );
      });
  }
}

interface ContainerTrackingData {
  airlineIataCode: string;
  forceAirline?: string;
  currencyCode?: string;
  departureDateStart?: string;
  promoCode?: string;
  siteEdition?: string;
  tagName?: string;
  deviceCategory?: string;
  v2DeepLinkApiSettings: Record<string, any>;
  language?: string;
  integrationSettings: any;
  meta: any;
  eventType: string;
  trackingConfig: any;
}
function isPopupComponent(eventType: string) {
  if (eventType !== undefined) {
    const upperCaseEventType = eventType.toUpperCase();
    return upperCaseEventType.includes('POPUP');
  }
  return false;
}

function handleActions(action: TrackingAction, container: ContainerTrackingData) {
  const { type, payload: state } = action;
  const { passengers, ...baseline } = buildBaseLineTrackingData(state, container);
  const { eventType } = container;
  let eventName;
  const isPopup = isPopupComponent(eventType);

  const enableEventsName =
    container.trackingConfig !== undefined
      ? container.trackingConfig.enable !== undefined
        ? container.trackingConfig.enable
        : []
      : [];

  switch (type) {
    case VerticalBookingActions.SEARCH_FLIGHTS: {
      const filteredData = {
        originAirportIataCode: formatValue(state.origin?.value),
        departureDate: formatDate(state.dates?.start),
        destinationAirportIataCode: formatValue(state.destination?.value)
      };
      sendTrackingEvent(
        'SEARCH_FLIGHTS_BOOKING',
        isPopup ? `em.fare.book.${eventType}` : `em.${eventType}.fsi`,
        baseline,
        filteredData,
        enableEventsName
      );
      redirect(container, { ...state, passengers });
      break;
    }

    case VerticalBookingActions.CHANGE_ORIGIN: {
      const filteredData = { originAirportIataCode: formatValue(state.origin?.value) };
      eventName = isPopup ? 'em.fare.popup.change-origin' : `em.${eventType}.change-origin`;
      sendTrackingEvent(
        'CHANGE_ORIGIN_BOOKING',
        eventName,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
    case VerticalBookingActions.CHANGE_AIRLINE: {
      const filteredData = { airlineCodes: state.airlines?.value };
      eventName = isPopup
        ? 'em.fare.popup.change-airlineCodes'
        : `em.${eventType}.change-airlineCodes`;
      sendTrackingEvent(
        'CHANGE_AIRLINE_BOOKING',
        eventName,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
    case VerticalBookingActions.CHANGE_DESTINATION: {
      const filteredData = { destinationAirportIataCode: formatValue(state.destination?.value) };
      eventName = isPopup
        ? 'em.fare.popup.change-destination'
        : `em.${eventType}.change-destination`;
      sendTrackingEvent(
        'CHANGE_DESTINATION_BOOKING',
        eventName,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
    case VerticalBookingActions.POPUP_OPENED: {
      const filteredData = { destinationAirportIataCode: formatValue(state.destination?.value) };
      sendTrackingEvent(
        'POPUP_OPENED_BOOKING',
        `em.${eventType}.opened`,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
    case VerticalBookingActions.POPUP_CLOSE: {
      const filteredData = { destinationAirportIataCode: formatValue(state.destination?.value) };
      sendTrackingEvent(
        'POPUP_CLOSE_BOOKING',
        `em.${eventType}.close`,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
    case VerticalBookingActions.CHANGE_DEPARTURE_DATE: {
      const filteredData = { destinationAirportIataCode: formatValue(state.destination?.value) };
      eventName = isPopup ? 'em.fare.popup.change-departure' : `em.${eventType}.change-departure`;
      sendTrackingEvent(
        'CHANGE_DEPARTURE_DATE_BOOKING',
        eventName,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
    case VerticalBookingActions.CHANGE_RETURN_DATE: {
      const filteredData = { destinationAirportIataCode: formatValue(state.destination?.value) };
      eventName = isPopup ? 'em.fare.popup.change-return' : `em.${eventType}.change-return`;
      sendTrackingEvent(
        'CHANGE_RETURN_DATE_BOOKING',
        eventName,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
    case VerticalBookingActions.CHANGE_JOURNEY_TYPE: {
      const filteredData = { journeyType: state?.flightType };
      eventName = isPopup
        ? 'em.fare.popup.change-journeyType'
        : `em.${eventType}.change-journeyType`;
      sendTrackingEvent(
        'CHANGE_JOURNEY_TYPE_BOOKING',
        eventName,
        baseline,
        filteredData,
        enableEventsName
      );
      break;
    }
  }
}

/**
 *  The hook useTracking return a track function
 *  that throw the different tracking events through actions
 */
export function useTrackingChange() {
  const container = useTracking() as ContainerTrackingData;

  const track = useCallback(
    (actionType: VerticalBookingActions, payload: TrackingPayload) => {
      const action: TrackingAction = { type: actionType, payload };
      return handleActions(action, container);
    },
    [container]
  );

  return track;
}
